import { Field, RuleProps, RuleType } from "react-querybuilder";
import PowersearchFieldValue from "./PowersearchFieldEditors/PowersearchFieldValue";
import { useCallback, useContext, useMemo, useState } from "react";
import PowersearchFieldSelector from "./PowersearchFieldSelector";
import classNames from "classnames";
import PowersearchOperatorSelector from "./PowersearchOperatorSelector";
import CiroButton, { CiroButtonStyleEnum } from "../../../../shared/CiroButton";
import PowerUserFiltersContext from "../../../../../contexts/PowerUserFiltersContext";
import { TrashIcon } from "@heroicons/react/24/outline";

export enum PowersearchFieldEnum {
  CompanyIndustry = "companyIndustry",
  JobTitle = "jobTitle",
  CompanyDescription = "companyDescription",
  LinkedInProfileKeywords = "linkedinProfileKeywords",
  ContactCity = "contactCity",
  ContactState = "contactState",
  ContactCountry = "contactCountry",
  CompanyCity = "companyCity",
  CompanyState = "companyState",
  CompanyCountryIso = "companyCountryIso",
  CompanyHeadCountRange = "companyHeadCountRange",
}

export const PowersearchFieldTitles: { [key in PowersearchFieldEnum]: string } =
  {
    [PowersearchFieldEnum.CompanyIndustry]: "Company industry",
    [PowersearchFieldEnum.JobTitle]: "Job title",
    [PowersearchFieldEnum.CompanyDescription]: "Company description",
    [PowersearchFieldEnum.LinkedInProfileKeywords]: "LinkedIn profile keywords",
    [PowersearchFieldEnum.ContactCity]: "Contact city",
    [PowersearchFieldEnum.ContactState]: "Contact state",
    [PowersearchFieldEnum.ContactCountry]: "Contact country",
    [PowersearchFieldEnum.CompanyCity]: "Company HQ city",
    [PowersearchFieldEnum.CompanyState]: "Company HQ state",
    [PowersearchFieldEnum.CompanyCountryIso]: "Company HQ country",
    [PowersearchFieldEnum.CompanyHeadCountRange]: "Company head count",
  };

export const QueryBuilderFields: Field[] = [
  {
    name: PowersearchFieldEnum.CompanyIndustry,
    label: "Industry",
    operators: [
      {
        name: "industryIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.JobTitle,
    label: "Job title",
    operators: [
      {
        name: "jobTitleIncludes",
        label: "includes",
        valueEditorType: "text",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.CompanyDescription,
    label: "Company description",
    operators: [
      {
        name: "companyDescriptionIncludes",
        label: "includes",
        valueEditorType: "text",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.LinkedInProfileKeywords,
    label: "LinkedIn profile keywords",
    operators: [
      {
        name: "linkedinProfileKeywords",
        label: "includes",
        valueEditorType: "text",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.ContactCity,
    label: "Contact city",
    operators: [
      {
        name: "cityIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.ContactState,
    label: "Contact region/state",
    operators: [
      {
        name: "stateIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.ContactCountry,
    label: "Contact country",
    operators: [
      {
        name: "countryIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.CompanyCity,
    label: "Company HQ city",
    operators: [
      {
        name: "cityIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.CompanyState,
    label: "Company HQ region/state",
    operators: [
      {
        name: "stateIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.CompanyCountryIso,
    label: "Company HQ country",
    operators: [
      {
        name: "countryIncludes",
        label: "includes any of",
        valueEditorType: "multiselect",
      },
    ],
  },
  {
    name: PowersearchFieldEnum.CompanyHeadCountRange,
    label: "Company head count",
    operators: [
      {
        name: "<=",
        label: "less than or equal to",
        valueEditorType: "number",
      },
      {
        name: ">=",
        label: "greater than or equal to",
        valueEditorType: "number",
      },
    ],
  },
];

const PowersearchQueryRule = ({
  disabled,
  rule,
  actions,
  path,
  schema,
}: RuleProps) => {
  const { showErrorMessages } = useContext(PowerUserFiltersContext);
  const getChangeHandler = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    (prop: Exclude<keyof RuleType, "id" | "path">) =>
      (value: any, context?: any) => {
        if (!disabled) {
          actions.onPropChange(prop, value, path, context);
        }
      },
    [disabled, path, actions],
  );

  const errorMessage = showErrorMessages
    ? (
        schema.validationMap[rule.id || ""] as {
          reasons: string[];
        }
      )?.reasons?.[0]
    : undefined;

  const onChangeField = useMemo(
    () => getChangeHandler("field"),
    [getChangeHandler],
  );
  const onChangeOperator = useMemo(
    () => getChangeHandler("operator"),
    [getChangeHandler],
  );
  // const onChangeMatchMode = useMemo(
  //   () => getChangeHandler("match"),
  //   [getChangeHandler],
  // );
  // const onChangeValueSource = useMemo(
  //   () => getChangeHandler("valueSource"),
  //   [getChangeHandler],
  // );
  const onChangeValue = useMemo(
    () => getChangeHandler("value"),
    [getChangeHandler],
  );

  const [isTrashHovered, setIsTrashHovered] = useState(false);

  return (
    <div
      className={classNames(
        "py-4",
        "flex",
        "items-start",
        "justify-between",
        "border",
        "rounded-lg",
        "-ml-2",
        "pl-2",
        {
          "border-red-200": isTrashHovered,
          "border-transparent": !isTrashHovered,
        },
      )}
    >
      <div
        className={classNames(
          "grid",
          "grid-cols-5",
          "gap-2",
          "items-start",
          "w-full",
        )}
      >
        <div
          className={classNames(
            "grid",
            "grid-cols-2",
            "gap-2",
            "items-center",
            "col-span-2",
          )}
        >
          <div>
            {disabled ? (
              QueryBuilderFields.find((field) => field.name === rule.field)
                ?.label
            ) : (
              <PowersearchFieldSelector
                selectedField={rule.field}
                setSelectedField={onChangeField}
                disabled={disabled}
              />
            )}
          </div>
          <div className="px-4">
            <PowersearchOperatorSelector
              selectedOperator={rule.operator}
              onChangeOperator={onChangeOperator}
              operators={
                QueryBuilderFields.find((field) => field.name === rule.field)
                  ?.operators as {
                  name: string;
                  label: string;
                  valueEditorType: string;
                }[]
              }
            />
          </div>
        </div>
        <div className={classNames("col-span-3")}>
          <PowersearchFieldValue
            disabled={disabled}
            operator={rule.operator}
            value={rule.value}
            field={rule.field as PowersearchFieldEnum}
            handleOnChange={onChangeValue}
            errorMessage={errorMessage}
          />
        </div>
      </div>
      <div className={classNames("flex", "items-center")}>
        <CiroButton
          analyticsField="Remove powersearch query rule"
          onClick={() => {
            actions.onRuleRemove(path);
          }}
          customClassName={classNames(
            "p-2",
            "hover:text-red-500",
            "text-gray-500",
          )}
          style={CiroButtonStyleEnum.UNSTYLED}
          onMouseEnter={() => {
            setIsTrashHovered(true);
          }}
          onMouseLeave={() => setIsTrashHovered(false)}
        >
          <TrashIcon className={classNames("w-4", "h-4", "cursor-pointer")} />
        </CiroButton>
      </div>
    </div>
  );
};

export default PowersearchQueryRule;
